import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Link } from 'react-router-dom';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { monitoringdata } from "common/data/demo/monitoring/monitoring";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ScatterChart, Scatter, ZAxis, BarChart, Bar } from 'recharts';

const Monitoring = () => {
  document.title = "Enterprise";
  const [showAllScans, setShowAllScans] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  const toggleModal = (card = null) => {
    setSelectedCard(card);
    setShowAllScans(!showAllScans);
  };

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await fetch(`https://api.compliance.cx/monitoring/${userId}`);
        const data = await response.json();
        setCardData(data);
        setLoading(false);
      } catch (error) {
        if (userId === "demo"){
        setCardData(monitoringdata);
        }
        console.error("Error fetching card data:", error);
        setLoading(false);
      }
    };

    fetchCardData();
  }, []);

  const toggleFixStatus = async (cardId) => {
    try {
      const currentCard = cardData.find(card => card.title === cardId);
      if (!currentCard) return;

      const newFixedStatus = !currentCard.status?.fixed;

      const response = await fetch(`https://api.compliance.cx/monitoring/status/${userId}/${cardId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fixed: newFixedStatus }),
      });

      if (response.ok) {
        setCardData(prevData =>
          prevData.map(card =>
            card.title === cardId
              ? {
                ...card,
                status: {
                  ...card.status,
                  fixed: newFixedStatus
                }
              }
              : card
          )
        );
      } else {
        console.error("Failed to update fix status on server");
      }
    } catch (error) {
      console.error("Error updating fix status:", error);
    }
  };

  const getCardColor = (exploitScore) => {
    if (exploitScore === "Critical") return "danger";
    if (exploitScore === "High") return "warning";
    if (exploitScore === "Medium") return "info";
    return "secondary";
  };

  const calculateSummary = () => {
    const summary = {
      totalVulnerabilities: cardData.length,
      fixedVulnerabilities: cardData.filter(card => card.status?.fixed).length,
      fixableVulnerabilities: cardData.filter(card => card.fix.versions.length > 0).length,
      categories: [...new Set(cardData.map(card => card.category))],
      highRiskItems: cardData.filter(card =>
        (card.exploitscore === "Critical" || card.exploitscore === "High") &&
        !card.status?.fixed
      ).length,
    };

    // Transform data for scatter plot
    const scatterData = cardData.map(item => ({
      name: item.title,
      exploitability: item.stats.exploitabilityScore,
      impact: item.stats.impactScore,
      base: item.stats.baseScore,
      category: item.category,
      fixed: item.status?.fixed,
      severity: item.exploitscore
    }));

    // Transform data for severity distribution
    const severityData = cardData.reduce((acc, item) => {
      const severity = item.exploitscore;
      const exists = acc.find(x => x.severity === severity);
      if (exists) {
        exists.count++;
        if (item.status?.fixed) exists.fixed++;
      } else {
        acc.push({
          severity,
          count: 1,
          fixed: item.status?.fixed ? 1 : 0
        });
      }
      return acc;
    }, []);

    return { ...summary, scatterData, severityData };
  };

  const getFixStatus = (fix) => {
    return {
      text: fix.versions.length > 0 ? `${fix.currentVersion} → ${fix.versions[0]}` : "No fix available",
      color: fix.versions.length > 0 ? "success" : "danger"
    };
  };

  const getArtifactIcon = (category) => {
    const iconMap = {
      'java-archive': 'bx bxl-java',
      'npm': 'bx bxl-nodejs',
      'python': 'bx bxl-python',
      'docker': 'bx bxl-docker',
      'default': 'bx bx-package'
    };

    const categoryLower = category.toLowerCase();
    for (const [key, value] of Object.entries(iconMap)) {
      if (categoryLower.includes(key)) return value;
    }
    return iconMap.default;
  };


  const summary = calculateSummary();
  const COLORS = {
    Critical: '#DC3545', // danger red
    High: '#FD7E14',    // orange
    Medium: '#FFC107',  // warning yellow
    Low: '#17A2B8',     // info blue
    fixed: '#28A745',   // success green
    total: '#4F46E5'    // purple
  };
  

  const transformVulnerabilityData = (data) => {
    const severityOrder = ['Critical', 'High', 'Medium', 'Low'];
    const statusGroups = data.reduce((acc, item) => {
      const status = item.status?.fixed ? 'Fixed' : 'Active';
      const severity = item.exploitscore;

      if (!acc[severity]) {
        acc[severity] = { severity, Active: 0, Fixed: 0 };
      }
      acc[severity][status]++;
      return acc;
    }, {});

    return severityOrder.map(severity => ({
      severity,
      Active: statusGroups[severity]?.Active || 0,
      Fixed: statusGroups[severity]?.Fixed || 0,
      Total: (statusGroups[severity]?.Active || 0) + (statusGroups[severity]?.Fixed || 0)
    }));
  };
  return (
    <div className="page-content">
      <Breadcrumbs breadcrumbItem="Monitoring" />

      {/* Summary Section */}
      <Row className="mb-4">
        <Col sm="3">
          <Card className="shadow-sm">
            <CardBody>
              <h5 className="text-muted mb-2">Total Vulnerabilities</h5>
              <h2>{summary.totalVulnerabilities.toLocaleString()}</h2>
              <i className="bx bx-shield-quarter float-end text-primary fs-3"></i>
            </CardBody>
          </Card>
        </Col>
        <Col sm="3">
          <Card className="shadow-sm">
            <CardBody>
              <h5 className="text-muted mb-2">Fixed</h5>
              <h2>{summary.fixedVulnerabilities.toLocaleString()}</h2>
              <i className="bx bx-check-circle float-end text-success fs-3"></i>
            </CardBody>
          </Card>
        </Col>
        <Col sm="3">
          <Card className="shadow-sm">
            <CardBody>
              <h5 className="text-muted mb-2">Fixable</h5>
              <h2>{summary.fixableVulnerabilities}</h2>
              <i className="bx bx-wrench float-end text-info fs-3"></i>
            </CardBody>
          </Card>
        </Col>
        <Col sm="3">
          <Card className="shadow-sm">
            <CardBody>
              <h5 className="text-muted mb-2">Active Risk Items</h5>
              <h2 className="text-danger">{summary.highRiskItems}</h2>
              <i className="bx bx-error-circle float-end text-danger fs-3"></i>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Analytics Section */}
      {cardData.length > 0 && (
        <>
          <Row className="mb-4">
          
            <Col>
              <Card className="shadow-sm">
                <CardBody>
                  <h5 className="mb-4">Vulnerability Status Trends</h5>
                  <div style={{ height: "400px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={transformVulnerabilityData(cardData)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" strokeWidth={0.5} />
                        <XAxis
                          dataKey="severity"
                          padding={{ left: 30, right: 30 }}
                        />
                        <YAxis />
                        <Tooltip
                          content={({ payload, label }) => {
                            if (payload && payload.length) {
                              return (
                                <div className="bg-white p-2 shadow-sm border">
                                  <p className="fw-bold mb-1">{label}</p>
                                  <p
                                    className="mb-1"
                                    style={{ color: COLORS.Critical }}
                                  >
                                    Active: {payload[0]?.value || 0}
                                  </p>
                                  <p
                                    className="mb-0"
                                    style={{ color: COLORS.fixed }}
                                  >
                                    Fixed: {payload[1]?.value || 0}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="Active"
                          stroke={COLORS.Critical}
                          name="Active Vulnerabilities"
                          strokeWidth={2}
                          dot={{ r: 6 }}
                          activeDot={{ r: 8 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="Fixed"
                          stroke={COLORS.fixed}
                          name="Fixed Vulnerabilities"
                          strokeWidth={2}
                          dot={{ r: 6 }}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}


      {/* High Risk Warning */}
      {summary.highRiskItems > 0 && (
        <Card className="mb-4 bg-danger text-white">
          <CardBody>
            <i className="bx bx-error-circle me-2"></i>
            {summary.highRiskItems} unfixed vulnerabilities require immediate attention due to high exploit risk.
          </CardBody>
        </Card>
      )}

      {/* Package Details */}
      {summary.categories.map((category) => (
        <div key={category} className="mt-4">
          <h4 className="mb-3">
            <i className={`${getArtifactIcon(category)} me-2`}></i>
            {category}
          </h4>
          <Row>
            {cardData
              .filter(card => card.category === category)
              .map((card, index) => {
                const fixStatus = getFixStatus(card.fix);
                const isFixed = Boolean(card.status?.fixed);
                const uniqueScans = new Set(card.scans.map(scan => scan.scanId));

                return (
                  <Col sm="4" key={index}>
                    <Card
                      className={`shadow-sm mb-4 border-${isFixed ? 'success' : getCardColor(card.exploitscore)}`}
                      style={{
                        borderLeftWidth: "8px",
                      }}
                    >
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5>{card.title}</h5>
                            <Badge color={fixStatus.color}>
                              {fixStatus.text}
                            </Badge>
                          </div>
                          <Button
                            color={isFixed ? "success" : "secondary"}
                            size="sm"
                            onClick={() => toggleFixStatus(card.title)}
                          >
                            <i className={`bx ${isFixed ? 'bx-check' : 'bx-x'} me-1`}></i>
                            {isFixed ? "Fixed" : "Not Fixed"}
                          </Button>
                        </div>
                        <div className="d-flex justify-content-around mt-3">
                          <div className="text-center">
                            <p className="text-muted mb-1">Impact</p>
                            <h6>{card.stats.impactScore}</h6>
                          </div>
                          <div className="text-center">
                            <p className="text-muted mb-1">CVSS</p>
                            <h6>{card.stats.baseScore}</h6>
                          </div>
                          <div className="text-center">
                            <p className="text-muted mb-1">Exploitability</p>
                            <h6>{card.stats.exploitabilityScore}</h6>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-top">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <p className="text-muted mb-0">Recent Scans:</p>
                            <span className="text-primary">
                              <strong>{uniqueScans.size}</strong> Affected
                            </span>
                          </div>
                          {/* Show only 2 most recent scans */}
                          {card.scans.slice(0, 2).map((scan, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center mb-1">
                              <Link
                                to={`/scanresults/${scan.scanId}`}
                                className="text-decoration-none"
                              >
                                <small className="text-primary">{scan.scanId}</small>
                              </Link>
                              <small className="text-muted">{new Date(scan.timestamp).toLocaleString()}</small>
                            </div>
                          ))}
                          {card.scans.length > 0 && (
                            <Button
                              color="link"
                              className="p-0 mt-2"
                              onClick={() => toggleModal(card)}
                            >
                              View All Scans
                            </Button>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}

            {/* Reactstrap Modal */}
            <Modal
              isOpen={showAllScans}
              toggle={() => toggleModal()}
              centered
              size="lg"
            >
              <ModalHeader toggle={() => toggleModal()}>
                <div className="d-flex justify-content-between w-100">
                  <span>All Affected Scans</span>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className="d-flex justify-content-between align-items-center p-2 mb-2  ">
                  <h4 className="text-muted">Total Affected Scans</h4>
                  <h4 >
                    <strong>
                      {selectedCard ? new Set(selectedCard.scans.map(scan => scan.scanId)).size : 0}
                    </strong>
                  </h4></div>

                {selectedCard && selectedCard.scans.map((scan, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-center p-2 mb-2 bg-secondary-subtle"
                    style={{
                      
                      borderRadius: '4px'
                    }}
                  >
                    <Link
                      to={`/scanresults/${scan.scanId}`}
                      className="text-decoration-none"
                    >
                      <span className="text-primary">{scan.scanId}</span>
                    </Link>
                    <span className="text-muted">{new Date(scan.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => toggleModal()}>
                  Close
                </Button>
              </ModalFooter>
            </Modal>
          </Row>
        </div>
      ))}
    </div>
  );
};

export default Monitoring;